import React, { useState, useEffect, useCallback } from 'react';
import type { FileInfo } from './types';
import { encodeText, decodeText, getByteCount, toUrlSafe } from './utils/base64';
import TextAreaWithControls from './components/TextAreaWithControls';
import FileControls from './components/FileControls';
import Preview from './components/Preview';
import SwapIcon from './components/icons/SwapIcon';

const dataUrlRegex = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,(.*)$/s;

const App: React.FC = () => {
  const [decoded, setDecoded] = useState<string>('');
  const [encoded, setEncoded] = useState<string>('');
  const [isUrlSafe, setIsUrlSafe] = useState<boolean>(false);
  const [withPadding, setWithPadding] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [mimeType, setMimeType] = useState<string>('text/plain');
  const [layoutSwapped, setLayoutSwapped] = useState<boolean>(false);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const handleDecodedChange = (text: string) => {
    setDecoded(text);
    setFileInfo(null);
    setFileBase64(null);
    setMimeType('text/plain');
    setError(null);
  };

  const handleEncodedChange = (text: string) => {
    const trimmedText = text.trim();
    setEncoded(trimmedText);
    setFileInfo(null);
    setFileBase64(null);

    if (trimmedText === '') {
      setDecoded('');
      setError(null);
      setMimeType('text/plain');
      return;
    }

    const dataUrlMatch = trimmedText.match(dataUrlRegex);
    let base64Data = trimmedText;
    let newMimeType = 'text/plain';

    if (dataUrlMatch) {
      newMimeType = dataUrlMatch[1];
      base64Data = dataUrlMatch[2];
    }
    
    setMimeType(newMimeType);

    try {
      // Decode with lenient url-safe character handling
      const decodedText = decodeText(base64Data, true);
      if (newMimeType.startsWith('text/') || newMimeType === 'application/json' || newMimeType === 'text/plain') {
        setDecoded(decodedText);
      } else {
        setDecoded(`[Binary data detected: ${newMimeType}]`);
      }
      setError(null);
    } catch (err) {
      setError('Invalid Base64 string.');
      setDecoded('');
    }
  };
  
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    const newFileInfo = { name: file.name, type: file.type, size: file.size };
    setFileInfo(newFileInfo);
    setMimeType(file.type);

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      const base64String = dataUrl.split(',')[1];
      
      setFileBase64(base64String);
      
      if (!file.type.startsWith('text/')) {
        setDecoded(`[Binary File Content: ${file.name}]`);
      } else {
        try {
          // Always decode as standard first from a file
          const decodedText = decodeText(base64String, false); 
          setDecoded(decodedText);
        } catch {
          setDecoded(`[Could not decode text file: ${file.name}]`);
        }
      }
      setIsUploading(false);
    };
    reader.onerror = () => {
      setError("Failed to read the file. Please try again.");
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const clearAll = () => {
      setDecoded('');
      setEncoded('');
      setError(null);
      setFileInfo(null);
      setFileBase64(null);
      setMimeType('text/plain');
  };
  
  const handleSwapLayout = () => {
    setLayoutSwapped(prev => !prev);
  }

  useEffect(() => {
    let sourceText = decoded;
    // When options change, re-encode from the appropriate source
    if (fileBase64) {
      // Re-encode from the raw file base64 if it exists
      let newEncoded = fileBase64;
      if (isUrlSafe) newEncoded = toUrlSafe(newEncoded);
      if (!withPadding) newEncoded = newEncoded.replace(/=+$/, '');
      setEncoded(newEncoded);
    } else {
      // Otherwise, re-encode from the decoded text input
      setEncoded(encodeText(sourceText, isUrlSafe, withPadding));
    }
  }, [decoded, fileBase64, isUrlSafe, withPadding]);

  const decodedPanel = (
    <TextAreaWithControls
      label="Decoded (Plain Text)"
      value={decoded}
      onChange={handleDecodedChange}
      onClear={clearAll}
      placeholder="Type or paste your text here..."
      byteCount={getByteCount(decoded)}
      isError={false}
      readOnly={isUploading || (!!fileInfo && !fileInfo.type.startsWith('text/'))}
    />
  );
  
  const encodedPanel = (
    <TextAreaWithControls
      label="Encoded (Base64)"
      value={encoded}
      onChange={handleEncodedChange}
      onClear={clearAll}
      placeholder="Paste your Base64 or data: URL here..."
      byteCount={encoded.length}
      isError={!!error}
      readOnly={isUploading}
    />
  );

  return (
    <div className="bg-gray-50 text-slate-800 font-sans p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <main>
          <FileControls 
            onFileSelect={handleFileSelect}
            fileInfo={fileInfo}
            isUrlSafe={isUrlSafe}
            onToggleUrlSafe={() => setIsUrlSafe(prev => !prev)}
            isPadded={withPadding}
            onTogglePadding={() => setWithPadding(prev => !prev)}
            isUploading={isUploading}
          />

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-center gap-6 min-h-[400px]">
            {layoutSwapped ? encodedPanel : decodedPanel}
            
            <div className="hidden lg:flex justify-center my-4 lg:my-0">
              <button
                onClick={handleSwapLayout}
                className="p-3 rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                aria-label="Swap layout direction"
              >
                <SwapIcon className={`w-6 h-6 transition-transform duration-300 ${layoutSwapped ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {layoutSwapped ? decodedPanel : encodedPanel}
          </div>
          {error && <p className="text-red-500 text-center mt-4 text-sm font-medium">{error}</p>}

          <Preview 
            encodedData={encoded}
            mimeType={mimeType}
            fileName={fileInfo?.name || 'download'}
          />
        </main>
        
        <footer className="text-center mt-6 text-gray-500 text-sm">
            <p>Built for web developers. All processing is done client-side.</p>
        </footer>
      </div>
    </div>
  );
};

export default App;
